<template>
    <div v-if="active" class="audio-recorder">
        <div ref="deleteZone" class="ar-delete-zone" :class="{ 'ar-delete-zone--armed': cancelling }">
            <button-tooltip top :tooltip="$t('common.delete')">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="ar-icon-btn" v-on="on" v-bind="attrs" :disabled="processing"
                        @click="cancel">
                        <v-icon size="16">$delete</v-icon>
                    </button>
                </template>
            </button-tooltip>
        </div>

        <div v-if="preparing" class="ar-preparing">{{ $t('common.preparing_microphone') }}</div>

        <template v-else>
            <span class="ar-dot"></span>
            <span class="ar-time" :class="{ 'ar-time--warning': isNearLimit }">{{ formattedTime }}</span>
            <div v-if="cancelling" class="ar-cancel-hint">{{ $t('common.release_to_delete') }}</div>
            <div v-else class="ar-levels">
                <span v-for="(level, index) in levels" :key="index" class="ar-level"
                    :style="{ transform: `scaleY(${level})` }"></span>
            </div>
        </template>

        <button-tooltip top :tooltip="$t('common.stop')">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="ar-stop-btn" v-on="on" v-bind="attrs" :disabled="preparing || processing"
                    @click="stop">
                    <v-progress-circular v-if="processing" indeterminate size="14" width="2" color="#0C0E12" />
                    <v-icon v-else size="15" color="#0C0E12">mdi-stop</v-icon>
                </button>
            </template>
        </button-tooltip>
    </div>
</template>

<script>
import ButtonTooltip from "@/components/common/ButtonTooltip";
import {
    supportedAudioMimeType,
    mediaExtension,
    canRecordMedia,
    toUniversalAudio,
    DEFAULT_AUDIO_MIME_TYPE,
    AUDIO_CONSTRAINTS,
    AUDIO_RECORDER_OPTIONS,
    MAX_AUDIO_SECONDS
} from '@/utils/Recorder'

const LEVELS_COUNT = 24
const METER_INTERVAL = 70
const WARNING_SECONDS = 15
const MIN_RECORDING_MS = 400
const DELETE_ZONE_MARGIN = 20

export default {
    name: 'AudioRecorder',
    components: { ButtonTooltip },
    props: {
        cancelling: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            preparing: false,
            recording: false,
            processing: false,
            seconds: 0,
            levels: new Array(LEVELS_COUNT).fill(0.08),
            stream: null,
            mediaRecorder: null,
            chunks: [],
            timer: null,
            audioContext: null,
            analyser: null,
            meterData: null,
            meterFrame: null,
            lastMeterAt: 0,
            session: 0,
            startedAt: 0,
            stopRequested: false
        }
    },
    computed: {
        active(vm = this) {
            return vm.preparing || vm.recording
        },
        isNearLimit(vm = this) {
            return MAX_AUDIO_SECONDS - vm.seconds <= WARNING_SECONDS
        },
        formattedTime(vm = this) {
            const minutes = Math.floor(vm.seconds / 60)
            const seconds = vm.seconds % 60
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }
    },
    beforeDestroy() {
        this.cleanup()
    },
    methods: {
        async start() {
            const vm = this

            if (vm.active) return
            if (!canRecordMedia()) return vm.toast(vm.$t('common.microphone_access_error'), 'error')

            vm.preparing = true
            vm.session++
            const session = vm.session
            vm.$emit('active', true)

            let stream = null
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS })
            } catch (e) {
                if (session === vm.session) vm.reset()
                return vm.toast(vm.$t('common.microphone_access_error'), 'error')
            }

            if (session !== vm.session) {
                stream.getTracks().forEach(track => track.stop())
                return
            }

            vm.stream = stream

            try {
                const mimeType = supportedAudioMimeType()
                const options = { ...AUDIO_RECORDER_OPTIONS }
                if (mimeType) options.mimeType = mimeType

                vm.mediaRecorder = new MediaRecorder(vm.stream, options)
                vm.chunks = []

                vm.mediaRecorder.ondataavailable = (e) => {
                    if (e.data?.size) vm.chunks.push(e.data)
                }
                vm.mediaRecorder.onstart = () => vm.onStart()
                vm.mediaRecorder.onstop = () => vm.onStop(vm.mediaRecorder?.mimeType || mimeType)
                vm.mediaRecorder.onerror = () => {
                    vm.cleanup()
                    vm.toast(vm.$t('common.microphone_access_error'), 'error')
                }

                vm.mediaRecorder.start()
            } catch (e) {
                vm.reset()
                vm.toast(vm.$t('common.microphone_access_error'), 'error')
            }
        },
        onStart() {
            const vm = this
            vm.preparing = false
            vm.recording = true
            vm.seconds = 0
            vm.startedAt = Date.now()
            vm.timer = setInterval(() => {
                vm.seconds++
                if (vm.seconds >= MAX_AUDIO_SECONDS) vm.stop()
            }, 1000)
            vm.startMeter()

            if (vm.stopRequested) {
                vm.stopRequested = false
                vm.stop()
            }
        },
        async onStop(mimeType) {
            const vm = this
            const type = mimeType || DEFAULT_AUDIO_MIME_TYPE
            const recorded = new Blob(vm.chunks, { type })
            const tooShort = Date.now() - vm.startedAt < MIN_RECORDING_MS

            vm.chunks = []
            vm.stopMeter()
            vm.stopStream()

            if (!recorded.size) return vm.reset()
            if (tooShort) {
                vm.reset()
                return vm.toast(vm.$t('common.recording_too_short'), 'error')
            }

            vm.processing = true
            const blob = await toUniversalAudio(recorded)
            const name = `audio_${Date.now()}.${mediaExtension(blob.type)}`
            const file = new File([blob], name, { type: blob.type })

            vm.reset()
            vm.$emit('record', { file, url: URL.createObjectURL(blob) })
        },
        stop() {
            const vm = this
            if (vm.preparing) {
                vm.stopRequested = true
                return
            }
            if (!vm.recording) return
            vm.recording = false
            vm.clearTimer()
            try {
                vm.mediaRecorder.stop()
            } catch (e) {
                vm.cleanup()
            }
        },
        cancel() {
            this.cleanup()
            this.$emit('cancel')
        },
        isOverDeleteZone(x, y) {
            const el = this.$refs.deleteZone
            if (!el) return false

            const rect = el.getBoundingClientRect()
            return x >= rect.left - DELETE_ZONE_MARGIN
                && x <= rect.right + DELETE_ZONE_MARGIN
                && y >= rect.top - DELETE_ZONE_MARGIN
                && y <= rect.bottom + DELETE_ZONE_MARGIN
        },
        cleanup() {
            const vm = this
            if (vm.mediaRecorder) {
                vm.mediaRecorder.onstop = null
                vm.mediaRecorder.onerror = null
                if (vm.recording) {
                    try { vm.mediaRecorder.stop() } catch (e) { /* noop */ }
                }
            }
            vm.chunks = []
            vm.stopMeter()
            vm.stopStream()
            vm.reset()
        },
        reset() {
            const vm = this
            vm.clearTimer()
            vm.preparing = false
            vm.recording = false
            vm.processing = false
            vm.stopRequested = false
            vm.seconds = 0
            vm.levels = new Array(LEVELS_COUNT).fill(0.08)
            vm.mediaRecorder = null
            vm.$emit('active', false)
        },
        clearTimer() {
            if (!this.timer) return
            clearInterval(this.timer)
            this.timer = null
        },
        stopStream() {
            const vm = this
            if (!vm.stream) return
            vm.stream.getTracks().forEach(track => track.stop())
            vm.stream = null
        },
        startMeter() {
            const vm = this
            const AudioContextClass = window.AudioContext || window.webkitAudioContext
            if (!AudioContextClass || !vm.stream) return

            try {
                vm.audioContext = new AudioContextClass()
                vm.analyser = vm.audioContext.createAnalyser()
                vm.analyser.fftSize = 512
                vm.audioContext.createMediaStreamSource(vm.stream).connect(vm.analyser)
                vm.meterData = new Uint8Array(vm.analyser.frequencyBinCount)
                vm.lastMeterAt = 0
                vm.meterFrame = requestAnimationFrame(vm.tickMeter)
            } catch (e) {
                vm.stopMeter()
            }
        },
        tickMeter(now) {
            const vm = this
            if (!vm.analyser) return

            if (now - vm.lastMeterAt >= METER_INTERVAL) {
                vm.lastMeterAt = now
                vm.analyser.getByteTimeDomainData(vm.meterData)

                let sum = 0
                for (let i = 0; i < vm.meterData.length; i++) {
                    const value = (vm.meterData[i] - 128) / 128
                    sum += value * value
                }

                const rms = Math.sqrt(sum / vm.meterData.length)
                const level = Math.min(1, Math.max(0.08, rms * 3.5))
                vm.levels = [...vm.levels.slice(1), level]
            }

            vm.meterFrame = requestAnimationFrame(vm.tickMeter)
        },
        stopMeter() {
            const vm = this
            if (vm.meterFrame) cancelAnimationFrame(vm.meterFrame)
            vm.meterFrame = null
            vm.analyser = null
            vm.meterData = null
            if (vm.audioContext) {
                vm.audioContext.close().catch(() => {})
                vm.audioContext = null
            }
        }
    }
}
</script>

<style scoped>
.audio-recorder {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 7px 8px;
}

.ar-delete-zone {
    flex-shrink: 0;
    border-radius: 8px;
    transition: background .15s, transform .15s;
}

.ar-delete-zone--armed {
    background: rgba(232, 92, 92, .16);
    transform: scale(1.15);
}

.ar-delete-zone--armed .ar-icon-btn {
    color: var(--v-error-base);
}

.ar-icon-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background .15s;
}

.ar-icon-btn .v-icon {
    color: inherit;
}

.ar-icon-btn:hover:not(:disabled) {
    background: var(--v-outline-base);
    color: var(--v-txtSecondary-base);
}

.ar-icon-btn:disabled {
    opacity: .5;
    cursor: default;
}

.ar-preparing {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.ar-cancel-hint {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--v-error-base);
}

.ar-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v-error-base);
    flex-shrink: 0;
    animation: ar-pulse 1s ease-in-out infinite;
}

@keyframes ar-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .25; }
}

.ar-time {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
}

.ar-time--warning {
    color: var(--v-error-base);
}

.ar-levels {
    flex: 1;
    min-width: 0;
    height: 24px;
    display: flex;
    align-items: center;
    gap: 2px;
    overflow: hidden;
}

.ar-level {
    flex: 1;
    height: 100%;
    min-width: 2px;
    border-radius: 2px;
    background: var(--v-secondary-base);
    transform: scaleY(.08);
    transition: transform .07s linear;
}

.ar-stop-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: var(--brand-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity .15s;
}

.ar-stop-btn:disabled {
    opacity: .6;
    cursor: default;
}

.ar-stop-btn:not(:disabled):hover {
    opacity: .85;
}
</style>
